import {
  feelingExperience,
  feelingExperienceFrame,
} from '../../../packages/domain/src/46-feeling-experiences.mjs';
import { feelingItem, feelingFrame } from '../../../packages/domain/src/44-feeling-items.mjs';
import { postImageAttributes } from './28-image-sources.mjs';

/** Own passive portrait playback. Only one clearly visible item runs; explicit
 * pause, keyboard focus and finite completion survive viewport re-entry. */
export function createFeelingItems(ctx) {
  const players = new Set();
  let active = null;
  let visibilityFrame = 0;
  function refreshVisibility() {
    visibilityFrame = 0;
    for (const player of players) player.refreshVisibility();
    coordinate();
  }
  function scheduleVisibility() {
    if (!visibilityFrame) visibilityFrame = requestAnimationFrame(refreshVisibility);
  }
  function listenVisibility(listen) {
    const method = listen ? 'addEventListener' : 'removeEventListener';
    document[method]('scroll', scheduleVisibility, { passive: true, capture: true });
    window[method]('resize', scheduleVisibility);
    window.visualViewport?.[method]('resize', scheduleVisibility);
    window.visualViewport?.[method]('scroll', scheduleVisibility);
  }
  function coordinate() {
    const winner =
      [...players]
        .filter((player) => player.eligible() && ![...players].some((item) => item.interacting()))
        .sort((a, b) => b.visibility - a.visibility)[0] ?? null;
    if (active !== winner) active?.pause();
    active = winner;
    active?.play();
  }
  function render(post, priority = false) {
    const config = feelingItem(post.feelingItem);
    if (!config || !post.image) return null;
    const experience = feelingExperience(post);
    if (config.mood !== 'calm' && !experience) {
      const poster = ctx.e('section', 'feeling-item');
      poster.append(
        ctx.field('img', {
          ...postImageAttributes(post.image, { priority }),
          alt: post.alt,
          width: 1024,
          height: 1536,
          className: 'feeling-image',
        }),
      );
      return poster;
    }
    const host = ctx.e('section', 'feeling-item feeling-story');
    host.dataset.feeling = config.mood;
    const canvas = ctx.e('div', 'feeling-canvas');
    canvas.tabIndex = 0;
    canvas.setAttribute('role', 'group');
    canvas.setAttribute(
      'aria-label',
      `${post.title ?? 'Visual moment'}. Left and right arrows change the scene; Space pauses or plays.`,
    );
    const layers = [0, 1].map(() =>
      ctx.field('img', {
        width: 1024,
        height: 1536,
        alt: '',
        className: 'feeling-scene-image',
      }),
    );
    layers.forEach((layer) => layer.setAttribute('aria-hidden', 'true'));
    const caption = ctx.e('div', 'feeling-story-caption');
    caption.setAttribute('role', 'group');
    const title = ctx.e('h3', 'feeling-story-title');
    const text = ctx.e('p', 'feeling-story-text');
    caption.append(title, text);
    const cue =
      config.mood === 'calm' ? ctx.e('div', `feeling-cue feeling-cue-${config.shape}`) : null;
    cue?.setAttribute('aria-hidden', 'true');
    const progress = ctx.e('div', 'feeling-story-progress');
    progress.setAttribute('aria-hidden', 'true');
    const segments = Array.from({ length: experience ? 4 : 2 }, () =>
      ctx.e('span', 'feeling-segment'),
    );
    const fills = segments.map((segment) => {
      const fill = ctx.e('span', 'feeling-segment-fill');
      segment.append(fill);
      return fill;
    });
    progress.append(...segments);
    const status = ctx.e('p', 'feeling-story-status');
    const pauseButton = control('pause', toggle, 'feeling-pause');
    const back = control('arrow-left', () => navigate(-1), 'feeling-edge feeling-back');
    const next = control('chevron-right', () => navigate(1), 'feeling-edge feeling-next');
    back.setAttribute('aria-label', 'Previous scene');
    next.setAttribute('aria-label', 'Next scene');
    canvas.append(...layers);
    if (cue) canvas.append(cue);
    canvas.append(caption, progress, back, next, pauseButton, status);
    host.append(canvas);
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    let running = false,
      activeMs = 0,
      startedAt = 0,
      frameId = 0,
      currentIndex = -1,
      displayedIndex = -1,
      layerIndex = 0,
      currentImage = '',
      requestToken = 0,
      disposed = false,
      imageStatus = '',
      complete = false,
      userPaused = false,
      pointer = null,
      lastPointerAt = -Infinity,
      suppressNativeClick = false,
      pendingToggleRunning = null,
      currentControlIcon = '';
    const player = {
      visibility: 0,
      interacting: () => !disposed && host.isConnected && player.visibility > 0 && Boolean(pointer),
      eligible: () =>
        !disposed &&
        !document.hidden &&
        !reduced.matches &&
        !userPaused &&
        !pointer &&
        imageStatus !== 'loading' &&
        !complete &&
        player.visibility >= 0.6 &&
        host.isConnected,
      pause,
      play,
      dispose: clean,
      refreshVisibility: measureVisibility,
    };
    if (!players.size) listenVisibility(true);
    players.add(player);
    function control(label, action, className) {
      const button = ctx.e('button', className);
      button.append(ctx.icon(label));
      button.type = 'button';
      button.addEventListener('click', (event) => {
        if (suppressNativeClick && event?.detail !== 0) {
          suppressNativeClick = false;
          event?.preventDefault?.();
          return;
        }
        suppressNativeClick = false;
        action();
      });
      return button;
    }
    function elapsed() {
      const time = activeMs + (running ? performance.now() - startedAt : 0);
      const stage = experience?.stages[currentIndex];
      return stage ? Math.min(time, stage.offsetMs + stage.durationMs) : time;
    }
    function showCaption(index, stage) {
      displayedIndex = index;
      title.textContent = experience ? stage.title : config.prompt;
      text.textContent = experience
        ? stage.text
        : 'Optional cue: breathe comfortably, without forcing or holding. Stop if uncomfortable.';
      caption.setAttribute('aria-label', `Scene ${index + 1} of ${segments.length}`);
      segments.forEach((segment, i) => {
        segment.dataset.current = String(i === index);
      });
    }
    function paintProgress() {
      const state = experience ? feelingExperienceFrame(experience, activeMs) : null;
      const clockIndex = state?.stageIndex ?? Math.floor(activeMs / 4000) % 2;
      const index = displayedIndex;
      const fraction =
        index === clockIndex ? (state?.stageProgress ?? (activeMs % 4000) / 4000) : 1;
      fills.forEach((fill, i) => {
        const value = i < index ? 1 : i === index ? fraction : 0;
        fill.style.transform = `scaleX(${Math.max(0, Math.min(1, value))})`;
      });
    }
    function paintStatus() {
      host.dataset.reducedMotion = String(reduced.matches);
      caption.setAttribute('aria-live', running ? 'off' : 'polite');
      status.setAttribute('aria-live', running ? 'off' : 'polite');
      status.dataset.notice = String(imageStatus === 'loading' || imageStatus === 'error');
      const label = running
        ? 'Pause story'
        : complete
          ? 'Replay story'
          : reduced.matches
            ? 'Motion disabled; navigate the still scenes'
            : 'Play story';
      const icon = running ? 'pause' : complete ? 'rotate-ccw' : 'play';
      if (icon !== currentControlIcon) {
        pauseButton.replaceChildren(ctx.icon(icon));
        currentControlIcon = icon;
      }
      pauseButton.setAttribute('aria-label', label);
      pauseButton.disabled = reduced.matches;
      back.disabled = experience ? currentIndex === 0 : activeMs < 4000;
      next.disabled = Boolean(experience && currentIndex === 3);
      status.textContent =
        imageStatus === 'loading'
          ? `Loading scene ${currentIndex + 1}${displayedIndex >= 0 ? ` · showing scene ${displayedIndex + 1}` : ''}`
          : imageStatus === 'error'
            ? 'Image unavailable · scene text remains available'
            : complete
              ? 'Sequence complete'
              : '';
    }
    function update() {
      const state = experience ? feelingExperienceFrame(experience, activeMs) : null;
      complete = state?.complete ?? false;
      const index = state?.stageIndex ?? Math.floor(activeMs / 4000) % 2;
      if (index !== currentIndex) {
        currentIndex = index;
        const stage = state?.stage ?? { image: post.image, artwork: { alt: post.alt } };
        const token = ++requestToken;
        if (stage.image === currentImage) {
          imageStatus = '';
          showCaption(index, stage);
        } else {
          imageStatus = 'loading';
          const incoming = 1 - layerIndex,
            image = layers[incoming];
          image.onload = () => {
            if (disposed || token !== requestToken) return;
            layerIndex = incoming;
            image.dataset.visible = 'true';
            image.alt = stage.artwork.alt;
            image.setAttribute('aria-hidden', 'false');
            layers[1 - incoming].dataset.visible = 'false';
            layers[1 - incoming].alt = '';
            layers[1 - incoming].setAttribute('aria-hidden', 'true');
            currentImage = stage.image;
            imageStatus = '';
            showCaption(index, stage);
            paintProgress();
            paintStatus();
            coordinate();
          };
          image.onerror = () => {
            if (disposed || token !== requestToken) return;
            layers.forEach((layer) => {
              layer.dataset.visible = 'false';
              layer.alt = '';
              layer.setAttribute('aria-hidden', 'true');
            });
            currentImage = '';
            imageStatus = 'error';
            showCaption(index, stage);
            paintProgress();
            paintStatus();
            coordinate();
          };
          Object.assign(image, postImageAttributes(stage.image, { priority: true }));
          if (image.complete && image.naturalWidth > 0) image.onload();
        }
      }
      if (cue && !reduced.matches) {
        const frame = feelingFrame(config, activeMs);
        cue.style.transform = `translate(-50%, -50%) scale(${frame.scale})`;
        layers[layerIndex].style.transform = `scale(${frame.imageScale})`;
        title.textContent = running ? `${frame.phase} · ${frame.count}` : config.prompt;
      }
      paintProgress();
      paintStatus();
    }
    function pause() {
      activeMs = elapsed();
      running = false;
      cancelAnimationFrame(frameId);
      update();
    }
    function tick(now) {
      if (!running) return;
      if (
        disposed ||
        !host.isConnected ||
        document.hidden ||
        reduced.matches ||
        player.visibility < 0.6
      ) {
        pause();
        return;
      }
      const boundary = experience
        ? experience.stages[currentIndex].offsetMs + experience.stages[currentIndex].durationMs
        : Infinity;
      activeMs = Math.min(boundary, activeMs + now - startedAt);
      startedAt = now;
      update();
      if (imageStatus === 'loading') {
        running = false;
        paintStatus();
        return;
      }
      if (complete) {
        running = false;
        paintStatus();
        coordinate();
        return;
      }
      frameId = requestAnimationFrame(tick);
    }
    function play() {
      if (!player.eligible() || running) return;
      running = true;
      startedAt = performance.now();
      paintStatus();
      frameId = requestAnimationFrame(tick);
    }
    function toggle() {
      if (reduced.matches) return;
      const wasRunning = pendingToggleRunning ?? running;
      pendingToggleRunning = null;
      if (wasRunning) {
        userPaused = true;
        pause();
      } else {
        userPaused = false;
        if (complete) {
          activeMs = 0;
          complete = false;
          update();
        }
        active?.pause();
        active = player;
        play();
      }
    }
    function navigate(direction) {
      pause();
      const index = Math.max(0, Math.min(3, currentIndex + direction));
      activeMs = experience
        ? experience.stages[index].offsetMs
        : Math.max(0, activeMs + direction * 4000);
      update();
      coordinate();
    }
    function focusChanged(event) {
      // Pointer focus follows the gesture's intent; keyboard focus holds the story
      // still until an explicit Play action, allowing untimed reading/navigation.
      if (pointer || performance.now() - lastPointerAt < 100) return;
      userPaused = true;
      pause();
      coordinate();
    }
    canvas.addEventListener('focusin', focusChanged);
    canvas.addEventListener('keydown', (event) => {
      if (event.target !== canvas) return;
      if (['ArrowLeft', 'ArrowRight', ' '].includes(event.key)) event.preventDefault();
      if (event.key === 'ArrowLeft') navigate(-1);
      if (event.key === 'ArrowRight') navigate(1);
      if (event.key === ' ') toggle();
    });
    canvas.addEventListener('pointerdown', (event) => {
      lastPointerAt = performance.now();
      if (event.isPrimary === false || (event.button != null && event.button !== 0)) return;
      suppressNativeClick = false;
      pendingToggleRunning = null;
      pointer = {
        x: event.clientX,
        y: event.clientY,
        at: performance.now(),
        running,
        button: event.target?.closest?.('button'),
      };
      pause();
      coordinate();
      if (!pointer.button) canvas.setPointerCapture?.(event.pointerId);
    });
    function release(event, cancelled = false) {
      if (!pointer) return;
      const held = pointer;
      pointer = null;
      const dx = event.clientX - held.x,
        dy = event.clientY - held.y;
      const duration = performance.now() - held.at;
      if (!cancelled && Math.abs(dx) >= 40 && Math.abs(dx) > Math.abs(dy) * 1.4) {
        suppressNativeClick = true;
        navigate(dx < 0 ? 1 : -1);
      } else if (!cancelled && Math.abs(dx) < 10 && Math.abs(dy) < 10 && duration < 350) {
        if (held.button) {
          if (held.button === pauseButton) pendingToggleRunning = held.running;
          return;
        }
        const rect = canvas.getBoundingClientRect();
        const position = (event.clientX - rect.left) / rect.width;
        if (position < 0.3) navigate(-1);
        else if (position > 0.7) navigate(1);
        else if (held.running) {
          userPaused = true;
          pause();
        } else toggle();
      } else {
        suppressNativeClick = true;
        if (held.running) coordinate();
      }
      if (canvas.hasPointerCapture?.(event.pointerId))
        canvas.releasePointerCapture(event.pointerId);
    }
    canvas.addEventListener('pointerup', (event) => release(event));
    canvas.addEventListener('pointercancel', (event) => release(event, true));
    canvas.addEventListener('lostpointercapture', (event) => release(event, true));
    function preferencesChanged() {
      pause();
      coordinate();
    }
    const visibilityChanged = () => coordinate();
    function measureVisibility() {
      if (disposed || !host.isConnected) {
        player.visibility = 0;
        return;
      }
      const rect = canvas.getBoundingClientRect();
      const viewport = window.visualViewport;
      const top = viewport?.offsetTop ?? 0,
        left = viewport?.offsetLeft ?? 0,
        height = viewport?.height ?? window.innerHeight,
        width = viewport?.width ?? window.innerWidth;
      if (![rect.top, rect.left, rect.height, rect.width, height, width].every(Number.isFinite))
        return;
      const visibleHeight = Math.max(
        0,
        Math.min(rect.top + rect.height, top + height) - Math.max(rect.top, top),
      );
      const visibleWidth = Math.max(
        0,
        Math.min(rect.left + rect.width, left + width) - Math.max(rect.left, left),
      );
      player.visibility =
        rect.height > 0 && rect.width > 0 && height > 0 && width > 0
          ? Math.min(
              visibleHeight / Math.min(rect.height, height),
              visibleWidth / Math.min(rect.width, width),
            )
          : 0;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        const available = Math.min(
          entry?.boundingClientRect?.height ?? 1,
          entry?.rootBounds?.height ?? window.innerHeight ?? 1,
        );
        player.visibility = entry?.isIntersecting
          ? entry.intersectionRect?.height != null
            ? entry.intersectionRect.height / available
            : (entry.intersectionRatio ?? 1)
          : 0;
        measureVisibility();
        coordinate();
      },
      { threshold: Array.from({ length: 21 }, (_, index) => index / 20) },
    );
    function clean() {
      pause();
      disposed = true;
      requestToken++;
      layers.forEach((layer) => {
        layer.onload = null;
        layer.onerror = null;
      });
      observer.disconnect();
      reduced.removeEventListener('change', preferencesChanged);
      document.removeEventListener('visibilitychange', visibilityChanged);
      players.delete(player);
      if (active === player) active = null;
      if (!players.size) {
        cancelAnimationFrame(visibilityFrame);
        visibilityFrame = 0;
        listenVisibility(false);
      } else coordinate();
    }
    update();
    reduced.addEventListener('change', preferencesChanged);
    document.addEventListener('visibilitychange', visibilityChanged);
    observer.observe(canvas);
    return host;
  }
  function dispose() {
    for (const player of [...players]) player.dispose();
  }
  return { render, dispose };
}
